const RELOAD_KEY = 'mfe_reload_attempted';
const RELOAD_TIMESTAMP_KEY = 'mfe_reload_timestamp';
const RELOAD_TIMEOUT = 60000;

const shouldAttemptReload = () => {
  const reloadAttempted = sessionStorage.getItem(RELOAD_KEY);
  const reloadTimestamp = sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);

  if (!reloadAttempted) {
    return true;
  }

  if (reloadTimestamp) {
    const timeSinceReload = Date.now() - parseInt(reloadTimestamp, 10);
    if (timeSinceReload > RELOAD_TIMEOUT) {
      sessionStorage.removeItem(RELOAD_KEY);
      sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
      return true;
    }
  }

  return false;
};

const attemptReload = reason => {
  if (shouldAttemptReload()) {
    console.warn(`[MFE] ${reason}. Attempting to reload page to clear cache...`);
    sessionStorage.setItem(RELOAD_KEY, 'true');
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, Date.now().toString());

    setTimeout(() => {
      window.location.reload();
    }, 100);

    return true;
  }

  return false;
};

const isRecoverableLoadError = errorMessage =>
  errorMessage.includes('ChunkLoadError') ||
  errorMessage.includes('Failed to fetch') ||
  errorMessage.includes('Loading script failed') ||
  errorMessage.includes('Loading chunk') ||
  errorMessage.includes('Unexpected token');

const isManifestUrl = url =>
  typeof url === 'string' &&
  (url.includes('mf-manifest.json') || url.includes('federation-manifest.json'));

const withFreshManifestFetch = (url, options = {}) => {
  if (!isManifestUrl(url)) {
    return { fetchUrl: url, fetchOptions: options };
  }

  const separator = url.includes('?') ? '&' : '?';
  return {
    fetchUrl: `${url}${separator}_t=${Date.now()}`,
    fetchOptions: { ...options, cache: 'no-store' }
  };
};

const mfeErrorHandlerPlugin = () => ({
  name: 'mfe-error-handler-plugin',
  async fetch(url, options) {
    const { fetchUrl, fetchOptions } = withFreshManifestFetch(url, options);

    try {
      const response = await fetch(fetchUrl, fetchOptions);
      if (!response.ok) {
        console.warn(`[MFE] Failed to fetch ${url}: ${response.status}`);

        if (response.status === 404 && attemptReload(`Remote resource not found (404) at ${url}`)) {
          return new Response('{}', { status: 200, headers: { 'Content-Type': 'application/json' } });
        }

        return new Response(
          JSON.stringify({
            id: 'fallback',
            name: 'fallback',
            metaData: {},
            remotes: [],
            shared: [],
            exposes: []
          }),
          {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      const clonedResponse = response.clone();
      const text = await clonedResponse.text();
      try {
        const json = JSON.parse(text);
        if (!json.id && !json.name && !json.metaData) {
          console.warn(`[MFE] Invalid manifest format at ${url}, using fallback`);
          return new Response(
            JSON.stringify({
              id: 'fallback',
              name: 'fallback',
              metaData: {},
              remotes: [],
              shared: [],
              exposes: []
            }),
            {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            }
          );
        }
      } catch (e) {
        console.warn(`[MFE] Non-JSON response at ${url}, using fallback`);
        return new Response(
          JSON.stringify({
            id: 'fallback',
            name: 'fallback',
            metaData: {},
            remotes: [],
            shared: [],
            exposes: []
          }),
          {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      return response;
    } catch (err) {
      console.warn(`[MFE] Network error fetching ${url}:`, err?.message || err);

      if (err?.message?.includes('Failed to fetch') || err?.message?.includes('Load failed')) {
        attemptReload(`Network error loading remote: ${err?.message}`);
      }

      return new Response(
        JSON.stringify({
          id: 'fallback',
          name: 'fallback',
          metaData: {},
          remotes: [],
          shared: [],
          exposes: []
        }),
        {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }
      );
    }
  },
  errorLoadRemote({ id, error, from, origin }) {
    console.warn(`[MFE] Failed to load remote module: ${id}`, { error: error?.message || error, from, origin });

    const errorMessage = error?.message || '';
    if (isRecoverableLoadError(errorMessage)) {
      attemptReload(`Critical module load error for remote "${id}": ${errorMessage}`);
    }

    return () => ({
      __esModule: true,
      default: {}
    });
  }
});

module.exports = mfeErrorHandlerPlugin;
